'use client';

import { FC, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useRouter } from 'next/navigation';
import UserCollaboratorDialog, { type UserCollaboratorCreatePreset } from '../customizing/UserCollaboratorDialog';
import { StandardDialog } from '@/components/common/StandardDialog';
import { FormTextField, FormAutocomplete, FormDatePicker } from '@/components/common/FormComponents';
import { FormLabel } from '@/components/common/FormLabel';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { OptionKey, type OptionMap } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { useBackendStore } from '@/stores/backendStore';
import { useSnackbar } from '@/hooks/useSnackbar';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { log } from '@/lib/utils/logger';
import { KeyValueType } from '@/lib/options/optionTypes';
import { IdLabelPair } from '@/types/common';
import {
  CollaboratorDto,
  PrivilegeEnum,
  ProjectDto,
  ProjectTypeDto,
  ProjectCategoryDto,
  ProjectStatusDto,
  RoleDto,
} from '@/api/ApiClient';
import type { CreateOrUpdateProjectRequestDto } from '@/api/ApiClient';
import { getProjectStatusDisplay, projectStatusSelectOptions } from '@/lib/types/projectTypes';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { isHtmlContentEmpty } from '@/lib/utils/textUtils';
import { ROLE_IDS } from '@/lib/utils/roleUtils';
import { filterAutocompleteOptionsByMatchRank } from '@/lib/utils/autocompleteMatchRank';
import { formatCollaboratorOptionLabel } from '@/lib/options/userUtils';
import { dedupeCollaboratorsByUserId } from '@/lib/users/selectableUsers';
import { useAuthStore } from '@/stores/authStore';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { usePrivileges } from '@/hooks/usePrivileges';

const OPTION_KEYS: OptionKey[] = [
  'users',
  'staff',
  'projectType',
  'projectCategory',
  'materials',
  'synthesisTechniques',
];

type UserCreateTarget = 'pi' | 'faculty' | 'staff';

function toUserOption(saved: CollaboratorDto): KeyValueType {
  return {
    key: saved.id!,
    value: formatCollaboratorOptionLabel(saved),
  };
}

function toUserOptions(users: CollaboratorDto[] | undefined): KeyValueType[] {
  return dedupeCollaboratorsByUserId(users)
    .filter((user) => user.id)
    .map(toUserOption);
}

const userPickerAutocompleteProps = {
  getOptionKey: (option: KeyValueType) => option.key,
  getOptionLabel: (option: KeyValueType) => option?.value || '',
  isOptionEqualToValue: (option: KeyValueType, value: KeyValueType) => option?.key === value?.key,
  filterOptions: (opts: KeyValueType[], params: { inputValue: string }) =>
    filterAutocompleteOptionsByMatchRank(opts, params, (o) => o.value),
  autoSelect: true,
  autoHighlight: true,
};

function mergeUniqueOptions(base: KeyValueType[], extras: KeyValueType[]): KeyValueType[] {
  if (!extras.length) return base;
  const seen = new Set(base.map((o) => o.key));
  const merged = [...base];
  for (const extra of extras) {
    if (!extra.key || seen.has(extra.key)) continue;
    seen.add(extra.key);
    merged.push(extra);
  }
  return merged;
}

function createPresetForTarget(target: UserCreateTarget): UserCollaboratorCreatePreset {
  switch (target) {
    case 'pi':
      return {
        userKind: 'external',
        lockUserKind: false,
        // External roles, in-house faculty, or in-house researchers (matches PI dropdown sources).
        rolePredicate: (role: RoleDto) => Boolean(role.isFaculty) || Boolean(role.isResearcher) || !role.isInhouse,
        helperText: 'PI may be an external user, faculty member, or in-house researcher.',
      };
    case 'faculty':
      return {
        userKind: 'internal',
        lockUserKind: true,
        rolePredicate: (role: RoleDto) => Boolean(role.isFaculty),
        helperText: 'Faculty requires an in-house faculty role.',
      };
    case 'staff':
      return {
        userKind: 'internal',
        lockUserKind: true,
        rolePredicate: (role: RoleDto) => Boolean(role.isResearcher),
        helperText: 'Staff requires an in-house researcher role.',
      };
  }
}

interface AddProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (project: ProjectDto) => void;
}

type AddProjectDialogOptions = Partial<
  Pick<OptionMap, 'users' | 'staff' | 'projectType' | 'projectCategory' | 'materials' | 'synthesisTechniques'>
>;

interface AddProjectDialogInnerProps extends AddProjectDialogProps {
  options: AddProjectDialogOptions;
}

const AddProjectDialogInner: FC<AddProjectDialogInnerProps> = ({ open, onClose, onSubmit, options }) => {
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();
  const router = useRouter();
  const systemConfig = useSystemConfig();
  const { isAuthorized } = usePrivileges();
  const canCreateUser = isAuthorized(PrivilegeEnum.User, 'create');

  // Form state
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [selectedUserPI, setSelectedUserPI] = useState<KeyValueType | null>(null);
  const [selectedProjectType, setSelectedProjectType] = useState<ProjectTypeDto | null>(null);
  const [selectedProjectCategory, setSelectedProjectCategory] = useState<KeyValueType | null>(null);
  const [selectedMaterials, setSelectedMaterials] = useState<IdLabelPair[]>([]);
  const [selectedTechniques, setSelectedTechniques] = useState<{ id: string; label: string }[]>([]);
  const [selectedFaculty, setSelectedFaculty] = useState<KeyValueType[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<KeyValueType[]>([]);
  const [title, setTitle] = useState<string>('');
  const [projectLabel, setProjectLabel] = useState<string>('');
  const [projectStatus, setProjectStatus] = useState<string>(ProjectStatusDto.Pending);
  const [isProjectLabelManuallyEdited, setIsProjectLabelManuallyEdited] = useState<boolean>(false);

  // Error states
  const [userPIError, setUserPIError] = useState(false);
  const [projectTypeError, setProjectTypeError] = useState(false);
  const [projectCategoryError, setProjectCategoryError] = useState(false);
  const [facultyError, setFacultyError] = useState(false);
  const [projectLabelError, setProjectLabelError] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [userCreateTarget, setUserCreateTarget] = useState<UserCreateTarget | null>(null);
  // Keep newly created users selectable before option caches refresh.
  const [createdUserExtras, setCreatedUserExtras] = useState<{
    pi: KeyValueType[];
    faculty: KeyValueType[];
    staff: KeyValueType[];
  }>({ pi: [], faculty: [], staff: [] });

  // Track user interaction - once user does anything, always show warning on close
  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  // Material options
  const materialOptions: IdLabelPair[] =
    options.materials
      ?.filter((m) => m.id !== undefined && m.name !== undefined && m.name !== 'None')
      .sort((a, b) => a.name!.localeCompare(b.name!))
      .map((m) => ({
        id: m.id!,
        label: m.name!,
      })) ?? [];

  // Technique options - only top-level techniques (no parent or groups)
  const techniqueOptions: { id: string; label: string }[] = useMemo(() => {
    if (!options.synthesisTechniques) return [];
    const allTechniques = options.synthesisTechniques.filter(
      (t) => t.id !== undefined && t.name !== undefined && t.name !== 'None'
    );
    // Filter to show only top-level techniques: those without a parent, or those that are groups
    const filtered = allTechniques.filter((item: any) => {
      const itemId = item.id;
      const hasParent = item.parentTechnique || item.parentID || item.parent;
      // If it has no parent, it's top-level
      if (!hasParent) return true;
      // If it's a group (has children - other techniques reference it as parent), also include it
      const isGroup = allTechniques.some((other: any) => {
        const otherParent = other.parentTechnique || other.parentID || other.parent;
        return otherParent === itemId;
      });
      return isGroup;
    });
    return filtered
      .sort((a, b) => a.name!.localeCompare(b.name!))
      .map((t) => ({
        id: t.id!,
        label: t.name!,
      }));
  }, [options.synthesisTechniques]);

  // PI options: community users plus in-house faculty and researchers, labeled Name (email).
  const userPIOptions = useMemo(() => {
    const externals = toUserOptions(options.users);
    const inHouse = toUserOptions(
      (options.staff ?? []).filter((user) => user.roles?.some((role) => role.isFaculty || role.isResearcher))
    );
    return mergeUniqueOptions(mergeUniqueOptions(externals, inHouse), createdUserExtras.pi);
  }, [options.users, options.staff, createdUserExtras.pi]);

  const facultySelectOptions = useMemo(
    () =>
      mergeUniqueOptions(
        toUserOptions((options.staff ?? []).filter((user) => user.roles?.some((role) => role.isFaculty))),
        createdUserExtras.faculty
      ),
    [options.staff, createdUserExtras.faculty]
  );

  const staffSelectOptions = useMemo(
    () =>
      mergeUniqueOptions(toUserOptions((options.staff ?? []).filter((user) => user.faculty)), createdUserExtras.staff),
    [options.staff, createdUserExtras.staff]
  );

  // Single implementation of project label generation (prefix + techniques/materials + getFreeLabel).
  // Throws if type has no prefix or backend fails. Used by both submit path and auto-fill.
  const buildProjectLabel = useCallback(
    async (
      type: ProjectTypeDto,
      techniques: { id: string; label: string }[],
      materials: IdLabelPair[]
    ): Promise<string> => {
      const rawPrefix = type.prefix;
      const prefix = rawPrefix?.split(',')[0]?.trim();
      if (!prefix) {
        throw new Error('Project type has no configured prefix; please enter a Project ID manually');
      }
      if (type.isInternal) {
        let label = prefix;
        const techniquePart = techniques
          .map((t) => t.label)
          .filter(Boolean)
          .join('_');
        if (techniquePart) label += `_${techniquePart}`;
        const materialPart = materials
          .map((m) => m.label)
          .filter(Boolean)
          .join('_');
        if (materialPart) label += `_${materialPart}`;
        return await backend.project.getFreeLabel(label, false);
      }
      return await backend.project.getFreeLabel(prefix, true);
    },
    [backend.project]
  );

  // Generates a required, validated project label at submit time when the user
  // has left the Project ID empty. Uses current form state and surfaces any
  // backend or configuration errors to the user via validation/snackbar.
  const generateProjectLabel = useCallback(async (): Promise<string> => {
    try {
      const type = selectedProjectType;
      if (!type) {
        throw new Error('Project type is required to generate label');
      }
      return await buildProjectLabel(type, selectedTechniques, selectedMaterials);
    } catch (error) {
      log.error('[AddProjectDialog] Failed to generate project label:', error);
      throw error;
    }
  }, [selectedProjectType, selectedTechniques, selectedMaterials, buildProjectLabel]);

  // Best-effort auto-generation of the Project ID while the user edits the form.
  // Invoked from field change handlers (type/techniques/materials) with the
  // freshest values; only runs when the user has NOT manually edited the Project ID.
  // Swallows errors (logs only) so it never blocks the user.
  const tryAutoGenerateLabel = useCallback(
    async (type: ProjectTypeDto | null, techniques: { id: string; label: string }[], materials: IdLabelPair[]) => {
      if (!isProjectLabelManuallyEdited && type) {
        try {
          const label = await buildProjectLabel(type, techniques, materials);
          setProjectLabel(label);
        } catch (error) {
          log.debug('[AddProjectDialog] Auto-generation failed:', error);
        }
      }
    },
    [isProjectLabelManuallyEdited, buildProjectLabel]
  );

  // Reset form to defaults when dialog closes
  const handleClose = () => {
    setSelectedDate(new Date());
    setSelectedEndDate(null);
    setSelectedUserPI(null);
    setSelectedProjectType(null);
    setSelectedProjectCategory(null);
    setSelectedMaterials([]);
    setSelectedTechniques([]);
    setSelectedFaculty([]);
    setSelectedStaff([]);
    setTitle('');
    setProjectLabel('');
    setProjectStatus(ProjectStatusDto.Pending);
    setIsProjectLabelManuallyEdited(false);
    setUserPIError(false);
    setProjectTypeError(false);
    setProjectCategoryError(false);
    setFacultyError(false);
    setProjectLabelError(false);
    setTitleError(false);
    setUserCreateTarget(null);
    setCreatedUserExtras({ pi: [], faculty: [], staff: [] });
    onClose();
  };

  const validateProjectLabel = async (label: string): Promise<{ isValid: boolean; message?: string }> => {
    if (!label.trim()) {
      return { isValid: false, message: 'Project ID is required' };
    }

    // Type-based validation
    const projectTypeCode = selectedProjectType?.code;
    if (projectTypeCode) {
      const allowedPrefixes = selectedProjectType.prefix?.split(',');
      if (allowedPrefixes && !allowedPrefixes.some((prefix) => label.startsWith(prefix))) {
        return {
          isValid: false,
          message: `Project ID must start with one of '${selectedProjectType.prefix}' for ${selectedProjectType?.label || projectTypeCode} projects`,
        };
      }
    }

    // Backend validation
    try {
      const result = await backend.project.isValidLabel(label);
      return { isValid: result.valid || false, message: result.error };
    } catch (error) {
      log.error('[AddProjectDialog] Failed to validate project label:', error);
      return { isValid: false };
    }
  };

  const validateAndBuildPayload = async (): Promise<CreateOrUpdateProjectRequestDto | null> => {
    let hasError = false;

    if (!selectedUserPI) {
      setUserPIError(true);
      hasError = true;
    } else {
      setUserPIError(false);
    }

    if (!selectedProjectType) {
      setProjectTypeError(true);
      hasError = true;
    } else {
      setProjectTypeError(false);
    }

    if (!selectedProjectType?.isInternal && !selectedProjectCategory) {
      setProjectCategoryError(true);
      hasError = true;
    } else {
      setProjectCategoryError(false);
    }

    // Faculty is only required for external (non-internal) projects
    if (!selectedProjectType?.isInternal && selectedFaculty.length === 0) {
      setFacultyError(true);
      hasError = true;
    } else {
      setFacultyError(false);
    }

    if (isHtmlContentEmpty(title)) {
      setTitleError(true);
      hasError = true;
    } else {
      setTitleError(false);
    }

    // Project label validation - must be either entered or generated
    if (!projectLabel.trim() && !selectedProjectType) {
      setProjectLabelError(true);
      hasError = true;
    } else {
      setProjectLabelError(false);
    }

    if (hasError) {
      showSnackbar('Please fill out all required fields.', 'error');
      return null;
    }

    // Generate project label if not provided by user
    let finalLabel: string;

    if (projectLabel.trim()) {
      // User provided a label - validate it
      const validation = await validateProjectLabel(projectLabel.trim());
      if (!validation.isValid) {
        showSnackbar(validation.message || 'Invalid project label', 'error');
        setProjectLabelError(true);
        return null;
      }
      finalLabel = projectLabel.trim();
    } else {
      // Generate label
      try {
        finalLabel = await generateProjectLabel();
      } catch (error: any) {
        showSnackbar(getProblemDetail(error) ?? 'Failed to generate project label', 'error');
        setProjectLabelError(true);
        return null;
      }
    }

    // Convert to CreateOrUpdateProjectRequest format
    // Backend expects type as string (code), not ProjectTypeDto object
    return {
      projectLabel: finalLabel,
      title: title.trim() || undefined,
      type: selectedProjectType!.code || undefined,
      category: (selectedProjectCategory?.key || undefined) as ProjectCategoryDto | undefined,
      projectStatus: projectStatus as ProjectStatusDto,
      startDate: selectedDate ?? new Date(),
      endDate: selectedEndDate || undefined,
      materialIds: selectedMaterials.map((m) => m.id),
      materials: selectedMaterials.map((m) => m.label),
      techniques: selectedTechniques.map((t) => t.id.toString()),
      collaborators: [
        // PI is always required
        { id: selectedUserPI!.key, roleId: ROLE_IDS.ProjectUserPI },

        // Faculty only applies to all projects
        ...selectedFaculty.map((f) => ({ id: f.key, roleId: ROLE_IDS.ProjectFaculty })),

        // Staff applies to all project types
        ...selectedStaff.map((s) => ({ id: s.key, roleId: ROLE_IDS.ProjectStaff })),
      ],
    };
  };

  const handleSubmit = async () => {
    const payload = await validateAndBuildPayload();
    if (!payload) return;

    try {
      const savedProject = await backend.project.create(payload);
      log.debug('[AddProjectDialog] Saved project:', savedProject);

      showSnackbar('Project added successfully.', 'success');

      // Call onSubmit callback if provided, passing the project data from payload
      if (onSubmit) {
        onSubmit(savedProject);
      }

      // Navigate to the newly created project before closing
      if (savedProject?.projectLabel) {
        router.push(`${systemConfig?.basePath ?? ''}/projects/${encodeURIComponent(savedProject.projectLabel)}`);
      }

      handleClose();
    } catch (error: unknown) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to create project.', 'error');
    }
  };

  const handleUserCreated = async (savedUser: CollaboratorDto) => {
    if (!savedUser?.id || !userCreateTarget) return;

    const option = toUserOption(savedUser);
    markAsInteracted();

    if (userCreateTarget === 'pi') {
      setCreatedUserExtras((prev) => ({ ...prev, pi: mergeUniqueOptions(prev.pi, [option]) }));
      setSelectedUserPI(option);
      setUserPIError(false);
      return;
    }

    if (userCreateTarget === 'faculty') {
      setCreatedUserExtras((prev) => ({ ...prev, faculty: mergeUniqueOptions(prev.faculty, [option]) }));
      setSelectedFaculty((prev) => (prev.some((f) => f.key === option.key) ? prev : [...prev, option]));
      setFacultyError(false);
      return;
    }

    setCreatedUserExtras((prev) => ({ ...prev, staff: mergeUniqueOptions(prev.staff, [option]) }));
    setSelectedStaff((prev) => (prev.some((s) => s.key === option.key) ? prev : [...prev, option]));
  };

  const openUserCreate = (target: UserCreateTarget) => setUserCreateTarget(target);

  const closeUserCreate = () => setUserCreateTarget(null);

  return (
    <>
      <StandardDialog
        open={open}
        onClose={handleClose}
        title="Add New Project"
        maxWidth="md"
        onSubmit={handleSubmit}
        submitText="Add Project"
        hasUnsavedChanges={hasUnsavedChanges}
        unsavedChangesMessage="You have unsaved changes to this project. Are you sure you want to discard them?"
      >
        {/* Project ID */}
        <FormTextField
          label="Project ID"
          required
          value={projectLabel}
          onChange={(e) => {
            const newValue = e.target.value;
            setProjectLabel(newValue);
            // Reset manual edit flag if user clears the label - allows auto-generation to work again
            if (!newValue.trim()) {
              setIsProjectLabelManuallyEdited(false);
            } else {
              setIsProjectLabelManuallyEdited(true);
            }
            markAsInteracted();
          }}
          size="small"
          fullWidth
          error={projectLabelError}
          placeholder="Enter Poject ID or Leave empty to auto-generate it"
          helperText="You can either provide an ID or leave it empty and a full ID will be generated automatically based on the project type, techniques, and materials."
        />

        {/* Type and Category */}
        <Box display="flex" gap={2}>
          <FormAutocomplete
            options={options.projectType ?? []}
            getOptionLabel={(option) => option?.label || ''}
            value={selectedProjectType}
            onChange={async (_, newValue) => {
              const newType = newValue || null;
              setSelectedProjectType(newType);
              markAsInteracted();
              // Auto-generate label when type changes
              await tryAutoGenerateLabel(newType, selectedTechniques, selectedMaterials);
            }}
            label="Type"
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            required
            error={projectTypeError}
            sx={{ flex: 1 }}
          />
          {!selectedProjectType?.isInternal ? (
            <FormAutocomplete
              options={options.projectCategory ?? []}
              getOptionLabel={(option) => option?.value || ''}
              value={selectedProjectCategory}
              onChange={(_, newValue) => {
                setSelectedProjectCategory(newValue || null);
                markAsInteracted();
              }}
              label="PI's Institution category"
              required
              error={projectCategoryError}
              sx={{ flex: 1 }}
            />
          ) : null}
        </Box>

        {/* Title */}
        <Box width="100%">
          <Typography variant="subtitle2" gutterBottom>
            <FormLabel required>Title</FormLabel>
          </Typography>
          <Box
            width="100%"
            sx={{
              '& .MuiPaper-root': {
                borderColor: titleError ? 'error.main' : 'divider',
                borderWidth: titleError ? 2 : 1,
              },
            }}
          >
            <RichTextEditor
              value={title}
              onChange={(value) => {
                setTitle(value);
                setTitleError(false);
                markAsInteracted();
              }}
            />
          </Box>
        </Box>

        {/* PI */}
        <Box display="flex" gap={1} alignItems="flex-start">
          <Box flex={1}>
            <FormAutocomplete
              options={userPIOptions}
              {...userPickerAutocompleteProps}
              renderOption={(props, option) => (
                <li {...props} key={option.key}>
                  {option.value}
                </li>
              )}
              value={selectedUserPI}
              onChange={(_, newValue) => {
                setSelectedUserPI(newValue || null);
                markAsInteracted();
              }}
              label="PI"
              required
              error={userPIError}
            />
          </Box>
          {canCreateUser ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => openUserCreate('pi')}
              startIcon={<AddIcon />}
              sx={{ minWidth: 'auto', px: 1, height: '40px', mt: 0.5 }}
              title="Create new user for PI"
            >
              Add
            </Button>
          ) : null}
        </Box>

        {/* Faculty - only shown for external (non-internal) projects */}
        {!selectedProjectType?.isInternal ? (
          <Box display="flex" gap={1} alignItems="flex-start">
            <Box flex={1}>
              <FormAutocomplete
                multiple
                options={facultySelectOptions}
                {...userPickerAutocompleteProps}
                renderOption={(props, option) => (
                  <li {...props} key={option.key}>
                    {option.value}
                  </li>
                )}
                value={selectedFaculty}
                onChange={(_, newValue) => {
                  setSelectedFaculty(newValue);
                  setFacultyError(false);
                  markAsInteracted();
                }}
                label="Faculty"
                required
                error={facultyError}
                helperText={facultyError ? 'At least one faculty member is required' : undefined}
              />
            </Box>
            {canCreateUser ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => openUserCreate('faculty')}
                startIcon={<AddIcon />}
                sx={{ minWidth: 'auto', px: 1, height: '40px', mt: 0.5 }}
                title="Create new faculty user"
              >
                Add
              </Button>
            ) : null}
          </Box>
        ) : null}

        {/* Staff */}
        <Box display="flex" gap={1} alignItems="flex-start">
          <Box flex={1}>
            <FormAutocomplete
              multiple
              options={staffSelectOptions}
              {...userPickerAutocompleteProps}
              renderOption={(props, option) => (
                <li {...props} key={option.key}>
                  {option.value}
                </li>
              )}
              value={selectedStaff}
              onChange={(_, newValue) => {
                setSelectedStaff(newValue);
                markAsInteracted();
              }}
              label="Staff"
            />
          </Box>
          {canCreateUser ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => openUserCreate('staff')}
              startIcon={<AddIcon />}
              sx={{ minWidth: 'auto', px: 1, height: '40px', mt: 0.5 }}
              title="Create new staff user"
            >
              Add
            </Button>
          ) : null}
        </Box>

        {/* Techniques */}
        <FormAutocomplete
          multiple
          options={techniqueOptions ?? []}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          value={selectedTechniques}
          onChange={async (_, newValue) => {
            setSelectedTechniques(newValue);
            markAsInteracted();
            // Auto-generate label when techniques change (for internal types)
            await tryAutoGenerateLabel(selectedProjectType, newValue, selectedMaterials);
          }}
          label="Techniques"
          autoSelect
          autoHighlight
        />

        {/* Materials */}
        <FormAutocomplete
          multiple
          options={materialOptions ?? []}
          getOptionLabel={(option) => option?.label || ''}
          filterOptions={(opts, params) =>
            filterAutocompleteOptionsByMatchRank(opts, params, (o) => String(o?.label ?? ''))
          }
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          value={selectedMaterials}
          onChange={async (_, newValue) => {
            setSelectedMaterials(newValue);
            markAsInteracted();
            // Auto-generate label when materials change (for internal types)
            await tryAutoGenerateLabel(selectedProjectType, selectedTechniques, newValue);
          }}
          label="Materials"
          autoSelect
          autoHighlight
        />

        {/* Project Status */}
        <FormAutocomplete
          options={projectStatusSelectOptions().map(({ value, label }) => ({ key: value, value: label }))}
          getOptionLabel={(option) => option.value}
          value={{
            key: projectStatus,
            value: getProjectStatusDisplay(projectStatus) || projectStatus,
          }}
          onChange={(_, newValue) => {
            setProjectStatus(newValue?.key || ProjectStatusDto.Pending);
            markAsInteracted();
          }}
          label="Project status"
        />

        {/* Start Date and End Date */}
        <Box display="flex" gap={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormDatePicker
              label="Start Date"
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                markAsInteracted();
              }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormDatePicker
              label="End Date"
              value={selectedEndDate}
              onChange={(newValue) => {
                setSelectedEndDate(newValue);
                markAsInteracted();
              }}
            />
          </LocalizationProvider>
        </Box>
      </StandardDialog>

      {/* User Creation Dialog — rendered beside the form so option-cache refresh cannot unmount it mid-save */}
      {userCreateTarget ? (
        <UserCollaboratorDialog
          open
          record={null}
          isEdit={false}
          createPreset={createPresetForTarget(userCreateTarget)}
          onSave={handleUserCreated}
          onClose={closeUserCreate}
        />
      ) : null}
    </>
  );
};

const AddProjectDialog: FC<AddProjectDialogProps> = (props) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const { currentUser } = useAuthStore();
  const dialogInitKey = `${currentUser?.id ?? 'anon'}|${currentUser?.selectedRole ?? ''}|${String(currentUser?.usingSignInAs ?? false)}`;
  // Keep the form mounted after the first load so invalidating users/staff (New User save)
  // does not tear down the dialog and leave a stuck MUI backdrop.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  if (!optionsLoading && !hasLoadedOnce) {
    setHasLoadedOnce(true);
  }

  if (optionsLoading && !hasLoadedOnce) {
    return (
      <StandardDialog
        open={props.open}
        onClose={props.onClose}
        title="Add Project"
        maxWidth="lg"
        onSubmit={() => {}}
        submitText="Add Project"
        submitDisabled
      >
        <Box sx={{ p: 2 }}>Loading options...</Box>
      </StandardDialog>
    );
  }

  return <AddProjectDialogInner key={dialogInitKey} {...props} options={options} />;
};

export { AddProjectDialog };
